import { useState, useEffect, useCallback } from 'react';

type UseRequestResult<T> = {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  retryFetchData: () => void;
};

const useRequest = <T,>(url: string): UseRequestResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setData(null);
      setError(null);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }
      const parsedData = (await response.json()) as T;
      setData(parsedData);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, retryFetchData: fetchData };
};

export default useRequest;
